<template>
  <div class="credential-page">
    <section class="page-header">
      <div class="container">
        <h1 class="page-title">{{ pageTitle }}</h1>
        <el-breadcrumb separator="/">
          <el-breadcrumb-item :to="{ path: '/' }">首页</el-breadcrumb-item>
          <el-breadcrumb-item :to="{ path: '/about' }">关于我们</el-breadcrumb-item>
          <el-breadcrumb-item>{{ pageTitle }}</el-breadcrumb-item>
        </el-breadcrumb>
      </div>
    </section>

    <section class="credential-content">
      <div class="container">
        <div class="credential-nav" aria-label="企业资质导航">
          <router-link to="/about/business-license">营业执照</router-link>
          <router-link to="/about/telecom-license">增值电信业务经营许可证</router-link>
        </div>

        <div v-if="type === 'business'" class="credential-card">
          <p class="notice">以下内容依据主管部门签发的营业执照原件展示，点击图片可放大查看。</p>
          <el-image
            class="business-license-image"
            :src="businessLicenseImage"
            fit="contain"
            :preview-src-list="[businessLicenseImage]"
            preview-teleported
          />
          <h2>登记信息</h2>
          <dl class="field-list">
            <template v-for="field in businessLicenseFields" :key="field.label">
              <dt>{{ field.label }}</dt>
              <dd>{{ field.value }}</dd>
            </template>
          </dl>
        </div>

        <div v-else class="credential-card">
          <div class="license-heading">
            <p class="notice">许可证共 8 页，以下图片均可点击放大查看。</p>
            <a
              class="pdf-link"
              :href="telecomLicensePdf"
              target="_blank"
              rel="noopener noreferrer"
            >在线查看原始 PDF</a>
          </div>
          <h2>许可证信息</h2>
          <dl class="field-list">
            <template v-for="field in telecomLicenseFields" :key="field.label">
              <dt>{{ field.label }}</dt>
              <dd>{{ field.value }}</dd>
            </template>
          </dl>
          <h2>获准经营的业务</h2>
          <ul class="business-items">
            <li v-for="item in telecomBusinessItems" :key="item">{{ item }}</li>
          </ul>
          <h2>完整许可证页面</h2>
          <div class="license-pages">
            <figure v-for="(page, index) in telecomLicensePages" :key="page">
              <el-image
                :src="page"
                fit="contain"
                :preview-src-list="telecomLicensePages"
                :initial-index="index"
                preview-teleported
              />
              <figcaption>第 {{ index + 1 }} / 8 页</figcaption>
            </figure>
          </div>
        </div>
      </div>
    </section>
  </div>
</template>

<script setup lang="ts">
import { computed } from "vue";
import {
  businessLicenseFields,
  businessLicenseImage,
  telecomBusinessItems,
  telecomLicenseFields,
  telecomLicensePages,
  telecomLicensePdf,
} from "@/data/companyCredentials";

const props = defineProps<{ type: "business" | "telecom" }>();
const pageTitle = computed(() =>
  props.type === "business" ? "营业执照" : "增值电信业务经营许可证",
);
</script>

<style scoped lang="scss">
.credential-page {
  padding-top: 80px;
  background: #f7f9fc;
}

.page-header {
  padding: 60px 0;
  color: #fff;
  background: linear-gradient(135deg, #1890ff 0%, #604fdf 100%);

  .page-title {
    margin-bottom: 12px;
    font-size: 36px;
  }

  :deep(.el-breadcrumb__inner),
  :deep(.el-breadcrumb__separator) {
    color: rgba(255, 255, 255, 0.86);
  }
}

.credential-content {
  padding: 48px 0 80px;
}

.credential-nav {
  display: flex;
  gap: 12px;
  margin-bottom: 24px;

  a {
    padding: 10px 18px;
    color: #334155;
    text-decoration: none;
    background: #fff;
    border: 1px solid #d9e2ef;
    border-radius: 8px;
  }

  a.router-link-active {
    color: #fff;
    background: var(--el-color-primary);
    border-color: var(--el-color-primary);
  }
}

.credential-card {
  padding: 28px;
  background: #fff;
  border-radius: 12px;
  box-shadow: 0 6px 24px rgba(30, 58, 95, 0.08);

  h2 {
    margin: 30px 0 18px;
    color: #1f2937;
    font-size: 24px;
  }
}

.notice {
  margin: 0;
  color: #475569;
  line-height: 1.7;
}

.business-license-image {
  display: block;
  width: min(100%, 1040px);
  margin: 20px auto 0;
  cursor: zoom-in;
}

.license-heading {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 20px;
}

.pdf-link {
  flex: 0 0 auto;
  padding: 10px 16px;
  color: #fff;
  text-decoration: none;
  background: var(--el-color-primary);
  border-radius: 8px;
}

.field-list {
  display: grid;
  grid-template-columns: 190px minmax(0, 1fr);
  margin: 0;
  overflow: hidden;
  border: 1px solid #e2e8f0;
  border-radius: 10px;

  dt,
  dd {
    margin: 0;
    padding: 14px 18px;
    line-height: 1.7;
    border-bottom: 1px solid #e2e8f0;
  }

  dt {
    color: #334155;
    font-weight: 600;
    background: #f8fafc;
  }

  dd {
    color: #475569;
    overflow-wrap: anywhere;
  }

  dt:last-of-type,
  dd:last-of-type {
    border-bottom: 0;
  }
}

.business-items {
  margin: 0;
  padding-left: 24px;
  color: #475569;
  line-height: 1.8;
}

.license-pages {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 22px;

  figure {
    margin: 0;
    padding: 14px;
    text-align: center;
    background: #f8fafc;
    border: 1px solid #e2e8f0;
    border-radius: 10px;
  }

  .el-image {
    width: 100%;
    cursor: zoom-in;
  }

  figcaption {
    margin-top: 10px;
    color: #64748b;
  }
}

@media (max-width: 768px) {
  .credential-page {
    padding-top: 60px;
  }

  .page-header {
    padding: 40px 0;

    .page-title {
      font-size: 28px;
    }
  }

  .credential-content {
    padding: 28px 0 48px;
  }

  .credential-nav,
  .license-heading {
    align-items: stretch;
    flex-direction: column;
  }

  .credential-card {
    padding: 18px;
  }

  .field-list {
    grid-template-columns: 1fr;

    dt,
    dd {
      border-bottom: 1px solid #e2e8f0;
    }

    dd:last-of-type {
      border-bottom: 0;
    }
  }

  .license-pages {
    grid-template-columns: 1fr;
  }
}
</style>
